/**
 * 全局 Toast 工具类
 * 基于 Vant Toast 组件封装的统一提示工具
 */

import { showToast as vantShowToast, showLoadingToast as vantShowLoadingToast, closeToast as vantCloseToast, type ToastOptions } from 'vant'
import { createApp } from 'vue'
import FancyToast from '@/components/feedback/FancyToast.vue'

// Toast 类型定义
export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading'

// 扩展的 Toast 配置
export interface CustomToastOptions extends Omit<ToastOptions, 'type'> {
  type?: ToastType
  duration?: number
  position?: 'top' | 'middle' | 'bottom'
  overlay?: boolean
  closeOnClick?: boolean
  closeOnClickOverlay?: boolean
}

// Toast 实例管理
class ToastManager {
  private currentToast: any = null
  private toastQueue: Array<() => void> = []
  private isShowing = false

  /**
   * 显示 Toast
   */
  show(message: string, options: CustomToastOptions = {}): void {
    const {
      type = 'info',
      duration = 2000,
      position = 'middle',
      overlay = false,
      closeOnClick = false,
      closeOnClickOverlay = false,
      ...restOptions
    } = options

    // 如果当前有 Toast 在显示，加入队列
    if (this.isShowing) {
      this.toastQueue.push(() => this.show(message, options))
      return
    }

    this.isShowing = true

    // 关闭之前的 Toast
    this.close()

    // 根据类型显示不同的 Toast
    switch (type) {
      case 'loading':
        this.currentToast = vantShowLoadingToast({
          message,
          duration: 0, // 加载 Toast 不自动关闭
          position,
          overlay,
          closeOnClick,
          closeOnClickOverlay,
          ...restOptions
        })
        break
      default:
        this.currentToast = vantShowToast({
          message,
          type: type as any,
          duration,
          position,
          overlay,
          closeOnClick,
          closeOnClickOverlay,
          ...restOptions
        })
    }

    // 设置自动关闭
    if (type !== 'loading' && duration > 0) {
      setTimeout(() => {
        this.close()
        this.processQueue()
      }, duration)
    }
  }

  /**
   * 显示成功提示
   */
  success(message: string, options: Omit<CustomToastOptions, 'type'> = {}): void {
    this.show(message, { ...options, type: 'success' })
  }

  /**
   * 显示错误提示
   */
  error(message: string, options: Omit<CustomToastOptions, 'type'> = {}): void {
    this.show(message, { ...options, type: 'error' })
  }

  /**
   * 显示警告提示
   */
  warning(message: string, options: Omit<CustomToastOptions, 'type'> = {}): void {
    this.show(message, { ...options, type: 'warning' })
  }

  /**
   * 显示信息提示
   */
  info(message: string, options: Omit<CustomToastOptions, 'type'> = {}): void {
    this.show(message, { ...options, type: 'info' })
  }

  /**
   * 显示加载提示
   */
  loading(message: string = '加载中...', options: Omit<CustomToastOptions, 'type'> = {}): void {
    this.show(message, { ...options, type: 'loading' })
  }

  /**
   * 关闭当前 Toast
   */
  close(): void {
    if (this.currentToast) {
      vantCloseToast()
      this.currentToast = null
    }
    this.isShowing = false
  }

  /**
   * 处理队列中的 Toast
   */
  private processQueue(): void {
    if (this.toastQueue.length > 0) {
      const nextToast = this.toastQueue.shift()
      if (nextToast) {
        // 延迟一点时间再显示下一个 Toast
        setTimeout(() => {
          nextToast()
        }, 100)
      }
    }
  }

  /**
   * 清空队列
   */
  clearQueue(): void {
    this.toastQueue = []
  }

  /**
   * 获取当前 Toast 状态
   */
  get isActive(): boolean {
    return this.isShowing
  }
}

// 创建全局实例
const toastManager = new ToastManager()

// 导出便捷方法
export const showToast = (message: string, options: CustomToastOptions = {}) => {
  toastManager.show(message, options)
}

export const showSuccessToast = (message: string, options: Omit<CustomToastOptions, 'type'> = {}) => {
  toastManager.success(message, options)
}

export const showErrorToast = (message: string, options: Omit<CustomToastOptions, 'type'> = {}) => {
  toastManager.error(message, options)
}

export const showWarningToast = (message: string, options: Omit<CustomToastOptions, 'type'> = {}) => {
  toastManager.warning(message, options)
}

export const showInfoToast = (message: string, options: Omit<CustomToastOptions, 'type'> = {}) => {
  toastManager.info(message, options)
}

export const showLoadingToast = (message: string = 'Loading...', options: Omit<CustomToastOptions, 'type'> = {}) => {
  toastManager.loading(message, options)
}

export const closeToast = () => {
  toastManager.close()
}

export const clearToastQueue = () => {
  toastManager.clearQueue()
}

// 自定义华丽提示（插入到 body，支持图标和副标题）
export const showFancyToast = (title: string, opts?: { subtitle?: string; icon?: string; duration?: number }) => {
  const container = document.createElement('div')
  document.body.appendChild(container)
  const app = createApp(FancyToast, {
    title,
    subtitle: opts?.subtitle || '',
    icon: opts?.icon || '',
    duration: opts?.duration ?? 5000
  })
  const vm = app.mount(container)
  // 自动卸载
  setTimeout(() => {
    app.unmount()
    document.body.removeChild(container)
  }, (opts?.duration ?? 5000) + 220)
  return vm
}

// 导出管理器实例
export { toastManager }

// 默认导出
export default {
  show: showToast,
  success: showSuccessToast,
  error: showErrorToast,
  warning: showWarningToast,
  info: showInfoToast,
  loading: showLoadingToast,
  close: closeToast,
  clearQueue: clearToastQueue,
  isActive: () => toastManager.isActive
}
